export type ChatSendMethod = {
  sendEmail: boolean;
  sendText: boolean;
};

const STORAGE_KEY = 'magicdoor.chatSendMethod.v1';
const MAX_STORED_CHATS = 200;

const FACTORY_DEFAULT_SEND_METHOD: ChatSendMethod = {
  sendEmail: false,
  sendText: true,
};

type StoredChatSendMethod = ChatSendMethod & {
  updatedAt?: number;
};

type StoredState = {
  chats: Record<string, StoredChatSendMethod>;
};

const generatePendingKey = (participantType: string, participantId: string) => {
  return `pending:${participantType}:${participantId}`;
};

const isSendMethod = (value: unknown): value is StoredChatSendMethod => {
  if (typeof value !== 'object' || value === null) return false;
  const method = value as StoredChatSendMethod;
  return typeof method.sendEmail === 'boolean' && typeof method.sendText === 'boolean';
};

const toSendMethod = (method: StoredChatSendMethod): ChatSendMethod => ({
  sendEmail: method.sendEmail,
  sendText: method.sendText,
});

const getStorage = (): Storage | undefined => {
  try {
    if (typeof localStorage === 'undefined') return undefined;
    return localStorage;
  } catch (error) {
    console.error(error);
    return undefined;
  }
};

const readState = (): StoredState => {
  try {
    const raw = getStorage()?.getItem(STORAGE_KEY);
    if (!raw) return { chats: {} };

    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return { chats: {} };

    const record = parsed as { chats?: unknown };
    const chats: Record<string, StoredChatSendMethod> = {};

    if (record.chats && typeof record.chats === 'object') {
      Object.entries(record.chats as Record<string, unknown>).forEach(([key, value]) => {
        if (isSendMethod(value)) chats[key] = value;
      });
    }

    return { chats };
  } catch (error) {
    console.error(error);
    return { chats: {} };
  }
};

const pruneChats = (chats: Record<string, StoredChatSendMethod>): Record<string, StoredChatSendMethod> => {
  const keys = Object.keys(chats);
  if (keys.length <= MAX_STORED_CHATS) return chats;

  const ranked = keys.sort((left, right) => (chats[left].updatedAt ?? 0) - (chats[right].updatedAt ?? 0));
  const next = { ...chats };
  ranked.slice(0, keys.length - MAX_STORED_CHATS).forEach((key) => {
    delete next[key];
  });
  return next;
};

const writeState = (state: StoredState) => {
  try {
    getStorage()?.setItem(STORAGE_KEY, JSON.stringify({ chats: pruneChats(state.chats) }));
  } catch (error) {
    console.error(error);
  }
};

export const getDefaultSendMethod = (): ChatSendMethod => {
  return FACTORY_DEFAULT_SEND_METHOD;
};

export const getSendMethodForChat = (
  chatId: string | null | undefined,
  participantType?: string,
  participantId?: string | null
): ChatSendMethod => {
  const chats = readState().chats;
  if (chatId && chats[chatId]) return toSendMethod(chats[chatId]);
  if (participantType && participantId) {
    const pending = chats[generatePendingKey(participantType, participantId)];
    if (pending) return toSendMethod(pending);
  }
  return getDefaultSendMethod();
};

export const setSendMethodForChat = (
  method: ChatSendMethod,
  chatId: string | null | undefined,
  participantType?: string,
  participantId?: string | null
) => {
  const key = chatId || (participantType && participantId ? generatePendingKey(participantType, participantId) : undefined);
  if (!key) return;

  const state = readState();
  writeState({
    chats: {
      ...state.chats,
      [key]: {
        ...method,
        updatedAt: Date.now(),
      },
    },
  });
};

export const migratePendingSendMethod = (participantType: string, participantId: string, chatId: string) => {
  const pendingKey = generatePendingKey(participantType, participantId);
  const state = readState();
  const pendingMethod = state.chats[pendingKey];
  if (!pendingMethod) return;

  const chats = { ...state.chats };
  delete chats[pendingKey];
  writeState({
    chats: {
      ...chats,
      [chatId]: {
        sendEmail: pendingMethod.sendEmail,
        sendText: pendingMethod.sendText,
        updatedAt: Date.now(),
      },
    },
  });
};
